/**
 * AdminDashboard — 工贸一体化管理后台
 * 包含左侧导航 + 多模块视图，权限按角色动态控制：
 *   sales   → 线索看板 + 销售订单 + 物流发货
 *   factory → BOM成本维护 + 生产看板
 *   admin   → 全部模块
 */
import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  LogOut, RefreshCw, Search, Inbox, Filter,
  CheckCircle2, Clock, FileText, XCircle,
  Building2, Tag, TrendingUp, Users, AlertCircle,
  ChevronDown, ChevronUp, LayoutDashboard, Factory,
  ShieldCheck, Menu, X, Pencil, Save, Loader2, Plus, Trash2,
  ShoppingCart, Wrench, Truck, BarChart3, GitBranch, FlaskConical,
  Boxes, PackageCheck, SendHorizonal, Settings2,
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { InquiryDrawer } from '@/components/InquiryDrawer';
import type { InquiryItem } from '@/components/InquiryDrawer';
import { OrdersView } from './AdminOrders';
import { ProductionView } from './AdminProduction';
import { LogisticsView } from './AdminLogistics';
import { OverviewView } from './AdminOverview';
import { TrackerView } from './AdminTracker';
import { RecipesView } from './AdminRecipes';
import { CustomersView } from './AdminCustomers';
import { BOMPageView } from './AdminBOM';

// ══════════════════════════════════════════════════
// 类型定义
// ══════════════════════════════════════════════════
type InquiryStatus = 'new' | 'quoted' | 'lost' | 'pending' | 'scheduling' | 'raw_material' | 'vulcanizing' | 'qc_passed' | 'packing' | 'shipped' | 'completed';
type UserRole = 'sales' | 'factory' | 'admin';
type ActiveTab = 'crm' | 'bom' | 'users' | 'orders' | 'production' | 'logistics' | 'overview' | 'tracker' | 'recipes' | 'customers';

interface Inquiry extends InquiryItem {
  status: InquiryStatus;
  updated_at: string;
}

interface UserProfile {
  id: string;
  email: string;
  role: UserRole;
  display_name: string | null;
  created_at: string;
}

// ══════════════════════════════════════════════════
// 状态配置
// ══════════════════════════════════════════════════
const STATUS_CONFIG: Record<InquiryStatus, { label: string; color: string; bg: string; icon: React.ElementType }> = {
  new:          { label: '新线索',   color: 'text-blue-600',   bg: 'bg-blue-100 border-blue-300',   icon: Inbox },
  quoted:       { label: '已报价',   color: 'text-purple-600', bg: 'bg-purple-100 border-purple-300', icon: FileText },
  lost:         { label: '已流失',   color: 'text-gray-500',   bg: 'bg-gray-100 border-gray-300',   icon: XCircle },
  pending:      { label: '待生产',   color: 'text-teal-700',   bg: 'bg-teal-100 border-teal-300',   icon: ShoppingCart },
  scheduling:   { label: '排产中',   color: 'text-indigo-600', bg: 'bg-indigo-100 border-indigo-300', icon: Settings2 },
  raw_material: { label: '原料备料', color: 'text-yellow-700', bg: 'bg-yellow-100 border-yellow-300', icon: Boxes },
  vulcanizing:  { label: '硫化/加工',color: 'text-orange-600', bg: 'bg-orange-100 border-orange-300', icon: Factory },
  qc_passed:    { label: '质检完成', color: 'text-cyan-700',   bg: 'bg-cyan-100 border-cyan-300',   icon: PackageCheck },
  packing:      { label: '包装待发', color: 'text-amber-700',  bg: 'bg-amber-100 border-amber-300', icon: Wrench },
  shipped:      { label: '已发货',   color: 'text-green-600',  bg: 'bg-green-100 border-green-300', icon: SendHorizonal },
  completed:    { label: '已完成',   color: 'text-green-800',  bg: 'bg-green-200 border-green-400', icon: CheckCircle2 },
};
const ALL_STATUSES = Object.keys(STATUS_CONFIG) as InquiryStatus[];

const ORANGE = 'oklch(0.66 0.19 42)';
const NAV_BG = '#F7F8FA';
const CARD_BG = '#FFFFFF';
const HEADER_BG = '#F0F2F5';
const ROW_HOVER = 'rgba(26,35,55,0.04)';

function timeAgo(iso: string) {
  const diff = Date.now() - new Date(iso).getTime();
  const h = Math.floor(diff / 3600000);
  if (h < 1) return `${Math.floor(diff / 60000)} 分钟前`;
  if (h < 24) return `${h} 小时前`;
  return `${Math.floor(h / 24)} 天前`;
}

function StatusBadge({ status }: { status: InquiryStatus }) {
  const cfg = STATUS_CONFIG[status];
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-sm border ${cfg.bg} ${cfg.color}`}>
      <Icon className="w-3 h-3" />{cfg.label}
    </span>
  );
}

function StatusSelect({ current, onChange, disabled }: {
  current: InquiryStatus; onChange: (s: InquiryStatus) => void; disabled: boolean;
}) {
  return (
    <select value={current} disabled={disabled}
      onChange={e => onChange(e.target.value as InquiryStatus)}
      className="text-xs rounded-sm px-2 py-1 outline-none disabled:opacity-50 cursor-pointer"
      style={{ background: '#FFFFFF', border: '1px solid #D0D5E2', color: '#1A2337' }}
    >
      {ALL_STATUSES.map(s => <option key={s} value={s}>{STATUS_CONFIG[s].label}</option>)}
    </select>
  );
}

// ══════════════════════════════════════════════════
// 手动录入线索弹窗
// ══════════════════════════════════════════════════
const MATERIAL_OPTIONS = [
  'FKM氟橡胶', 'EPDM三元乙丙', 'NBR丁腈橡胶',
  'NR天然橡胶', 'CR氯丁橡胶', 'ESD防静电橡胶',
];

const INPUT_STYLE: React.CSSProperties = {
  background: '#FFFFFF', border: '1px solid #D0D5E2',
  color: '#1A2337', borderRadius: '4px',
  padding: '8px 12px', fontSize: '13px', outline: 'none', width: '100%',
};

function AddLeadModal({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const [form, setForm] = useState({
    name: '', company: '', email: '', phone: '', material: '', message: '',
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const set = (k: string, v: string) => setForm(f => ({ ...f, [k]: v }));

  const validate = () => {
    if (!form.name.trim()) return '姓名不能为空';
    if (!form.phone.trim()) return '电话不能为空';
    if (!form.message.trim()) return '咨询内容不能为空';
    return '';
  };

  const handleSave = async () => {
    const e = validate();
    if (e) { setErr(e); return; }
    setSaving(true); setErr('');
    // 生成唯一业务编号 BY-YYYYMMDD-SEQ-随机4位
    const _now = new Date();
    const _dateStr = _now.toISOString().slice(0,10).replace(/-/g,'');
    const _startOfDay = `${_now.toISOString().slice(0,10)}T00:00:00`;
    const { count: _todayCount } = await supabase
      .from('inquiries')
      .select('id', { count: 'exact', head: true })
      .gte('created_at', _startOfDay);
    const _seq = String((_todayCount ?? 0) + 1).padStart(3, '0');
    const _rand4 = String(Math.floor(1000 + Math.random() * 9000));
    const biz_no = `BY-${_dateStr}-${_seq}-${_rand4}`;
    const { error } = await supabase.from('inquiries').insert([{
      name: form.name.trim(),
      company: form.company.trim() || null,
      email: form.email.trim() || null,
      phone: form.phone.trim(),
      material: form.material || null,
      message: form.message.trim(),
      status: 'new',
      biz_no,
    }]);
    setSaving(false);
    if (error) { setErr(error.message); return; }
    // 自动录入客户（独立 try-catch，不影响线索录入）
    try {
      const phone = form.phone.trim();
      const email = form.email.trim();
      const company = form.company.trim() || null;
      let isDuplicate = false;
      if (phone) {
        const { data: d1 } = await supabase.from('customers').select('id').eq('phone', phone).limit(1);
        if (d1 && d1.length > 0) isDuplicate = true;
      }
      if (!isDuplicate && email) {
        const { data: d2 } = await supabase.from('customers').select('id').eq('email', email).limit(1);
        if (d2 && d2.length > 0) isDuplicate = true;
      }
      if (!isDuplicate && company) {
        const { data: d3 } = await supabase.from('customers').select('id').eq('company', company).limit(1);
        if (d3 && d3.length > 0) isDuplicate = true;
      }
      if (!isDuplicate) {
        await supabase.from('customers').insert([{
          name: form.name.trim(),
          company,
          phone: phone || null,
          email: email || null,
          notes: '来源：后台手动录入线索',
        }]);
      }
    } catch (custErr) {
      console.warn('客户自动录入失败（不影响线索录入）:', custErr);
    }
    onSaved();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.75)' }}>
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
        className="w-full max-w-lg rounded-sm border border-border p-6 space-y-4"
        style={{ background: '#F7F8FA' }}
      >
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-bold" style={{ color: '#1A2337' }}>手动录入线索</h3>
            <p className="text-xs mt-0.5" style={{ color: '#5A6780' }}>适用于线下拜访、展会、电话来源的客户</p>
          </div>
          <button onClick={onClose}><X className="w-4 h-4" style={{ color: '#5A6780' }} /></button>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs mb-1.5" style={{ color: '#5A6780' }}>姓名 *</label>
            <input value={form.name} onChange={e => set('name', e.target.value)} style={INPUT_STYLE} placeholder="客户姓名" />
          </div>
          <div>
            <label className="block text-xs mb-1.5" style={{ color: '#5A6780' }}>公司名称</label>
            <input value={form.company} onChange={e => set('company', e.target.value)} style={INPUT_STYLE} placeholder="可选" />
          </div>
          <div>
            <label className="block text-xs mb-1.5" style={{ color: '#5A6780' }}>电话 *</label>
            <input value={form.phone} onChange={e => set('phone', e.target.value)} style={INPUT_STYLE} placeholder="+86 138 0000 0000" />
          </div>
          <div>
            <label className="block text-xs mb-1.5" style={{ color: '#5A6780' }}>邮箱</label>
            <input type="email" value={form.email} onChange={e => set('email', e.target.value)} style={INPUT_STYLE} placeholder="可选" />
          </div>
        </div>

        <div>
          <label className="block text-xs mb-1.5" style={{ color: '#5A6780' }}>感兴趣的材料</label>
          <select value={form.material} onChange={e => set('material', e.target.value)} style={INPUT_STYLE}>
            <option value="">— 未指定 —</option>
            {MATERIAL_OPTIONS.map(m => <option key={m} value={m}>{m}</option>)}
          </select>
        </div>

        <div>
          <label className="block text-xs mb-1.5" style={{ color: '#5A6780' }}>咨询内容 *</label>
          <textarea value={form.message} onChange={e => set('message', e.target.value)}
            rows={3} style={{ ...INPUT_STYLE, resize: 'none' }}
            placeholder="记录客户需求、应用场景、规格要求等…" />
        </div>

        {err && <p className="text-xs text-red-400 flex items-center gap-1"><AlertCircle className="w-3.5 h-3.5" />{err}</p>}

        <div className="flex gap-3 pt-1">
          <button onClick={handleSave} disabled={saving}
            className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-sm font-bold text-sm disabled:opacity-60"
            style={{ background: ORANGE, color: '#fff' }}
          >{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}录入线索</button>
          <button onClick={onClose} className="px-4 py-2.5 rounded-sm border border-border text-sm" style={{ color: '#5A6780' }}>取消</button>
        </div>
      </motion.div>
    </div>
  );
}

// ══════════════════════════════════════════════════
// 子视图：线索 CRM 看板
// ══════════════════════════════════════════════════
function CRMView({
  onConvertToOrder,
}: {
  onConvertToOrder?: (inquiryId: string) => void;
}) {
  const [inquiries, setInquiries] = useState<Inquiry[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [filterStatus, setFilterStatus] = useState<InquiryStatus | 'all'>('all');
  const [search, setSearch] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [drawerItem, setDrawerItem] = useState<Inquiry | null>(null);
  const [showAddLead, setShowAddLead] = useState(false);
  const [repeatBuyerIds, setRepeatBuyerIds] = useState<Set<string>>(new Set());

const fetchInquiries = useCallback(async (showRefresh = false) => {
    if (showRefresh) setRefreshing(true); else setLoading(true);
    const [{ data, error }, { data: customers }] = await Promise.all([
      supabase.from('inquiries').select('*').order('created_at', { ascending: false }),
      supabase.from('customers').select('phone,email,company,created_at'),
    ]);
    if (!error && data) {
      setInquiries(data as Inquiry[]);
      if (customers && customers.length > 0) {
        const ids = new Set<string>();
        (data as Inquiry[]).forEach(inq => {
          const inqTime = new Date(inq.created_at).getTime();
          const hasEarlierRecord = (customers as Record<string, string>[]).some(cu => {
            const cuTime = new Date(cu.created_at).getTime();
            const isMatch =
              (inq.phone   && cu.phone   === inq.phone)   ||
              (inq.email   && cu.email   === inq.email)   ||
              (inq.company && cu.company === inq.company);
            return isMatch && cuTime < inqTime - 5000;
          });
          if (hasEarlierRecord) ids.add(inq.id);
        });
        setRepeatBuyerIds(ids);
      }
    }
    setLoading(false); setRefreshing(false);
  }, []);

  useEffect(() => { fetchInquiries(); }, [fetchInquiries]);

  const handleStatusChange = async (id: string, status: InquiryStatus) => {
    await supabase.from('inquiries').update({ status }).eq('id', id);
    setInquiries(prev => prev.map(i => i.id === id ? { ...i, status } : i));
  };

  const filtered = inquiries.filter(i => {
    const matchStatus = filterStatus === 'all' || i.status === filterStatus;
    const q = search.toLowerCase();
    const matchSearch = !q || [i.name, i.email, i.phone, i.company ?? '', i.material ?? ''].some(v => v.toLowerCase().includes(q));
    return matchStatus && matchSearch;
  });

  const totalNew = inquiries.filter(i => i.status === 'new').length;
  const totalCompleted = inquiries.filter(i => i.status === 'completed').length;
  const convRate = inquiries.length > 0 ? Math.round((totalCompleted / inquiries.length) * 100) : 0;

  return (
    <div className="space-y-6">
      {/* 摘要卡 */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { icon: Inbox,        label: '新线索', value: totalNew,         color: '#60A5FA' },
          { icon: Users,        label: '总线索', value: inquiries.length, color: '#1A2337' },
          { icon: CheckCircle2, label: '已完成', value: totalCompleted,   color: ORANGE },
          { icon: TrendingUp,   label: '转化率', value: `${convRate}%`,   color: '#1A2337' },
        ].map(({ icon: Icon, label, value, color }) => (
          <div key={label} className="rounded-sm border border-border p-4" style={{ background: CARD_BG }}>
            <div className="flex items-center gap-2 mb-2">
              <Icon className="w-4 h-4" style={{ color }} />
              <span className="text-xs font-mono" style={{ color: '#5A6780' }}>{label}</span>
            </div>
            <span className="text-3xl font-black" style={{ color }}>{value}</span>
          </div>
        ))}
      </div>

      {/* 筛选 + 搜索 */}
      <div className="flex flex-wrap gap-2">
        <button onClick={() => setFilterStatus('all')}
          className={`text-xs font-medium px-3 py-1.5 rounded-sm border transition-colors ${filterStatus === 'all' ? 'border-orange-400/40 text-orange-400' : 'border-border text-gray-400 hover:text-white'}`}
          style={filterStatus === 'all' ? { background: `${ORANGE}18` } : {}}
        ><Filter className="w-3 h-3 inline mr-1" />全部 ({inquiries.length})</button>
        {ALL_STATUSES.map(s => {
          const cfg = STATUS_CONFIG[s];
          const Icon = cfg.icon;
          const active = filterStatus === s;
          return (
            <button key={s} onClick={() => setFilterStatus(s)}
              className={`inline-flex items-center gap-1 text-xs font-medium px-3 py-1.5 rounded-sm border transition-colors ${active ? `${cfg.bg} ${cfg.color}` : 'border-border text-gray-400 hover:text-white'}`}
            ><Icon className="w-3 h-3" />{cfg.label} ({inquiries.filter(i => i.status === s).length})</button>
          );
        })}
      </div>

      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none" style={{ color: '#5A6780' }} />
          <input type="search" value={search} onChange={e => setSearch(e.target.value)}
            placeholder="搜索姓名、邮箱、公司…"
            className="w-full pl-9 pr-4 py-2 rounded-sm text-sm outline-none"
            style={{ background: '#FFFFFF', border: '1px solid #D0D5E2', color: '#1A2337' }}
          />
        </div>
        <button onClick={() => fetchInquiries(true)} disabled={refreshing}
          className="flex items-center gap-1.5 text-xs border rounded-sm px-3 py-2 transition-colors disabled:opacity-50"
          style={{ borderColor: '#D0D5E2', color: '#5A6780' }}
        ><RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} /></button>
        <button onClick={() => setShowAddLead(true)}
          className="flex items-center gap-1.5 text-xs font-bold px-4 py-2 rounded-sm whitespace-nowrap"
          style={{ background: ORANGE, color: '#fff' }}
        ><Plus className="w-3.5 h-3.5" />新增线下线索</button>
      </div>

      {/* 表格 */}
      {loading ? (
        <div className="rounded-sm border border-border p-16 flex flex-col items-center gap-4" style={{ background: CARD_BG, color: '#5A6780' }}>
          <RefreshCw className="w-8 h-8 animate-spin" style={{ color: ORANGE }} />
          <span className="text-sm">加载线索数据中…</span>
        </div>
      ) : filtered.length === 0 ? (
        <div className="rounded-sm border border-border p-16 flex flex-col items-center gap-3" style={{ background: CARD_BG, color: '#5A6780' }}>
          <AlertCircle className="w-10 h-10" /><span className="font-medium">暂无匹配的线索</span>
        </div>
      ) : (
        <div className="rounded-sm border border-border overflow-hidden" style={{ background: CARD_BG }}>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border" style={{ background: HEADER_BG }}>
                  {['线索编号', '姓名 / 公司', '联系方式', '材料', '状态', '时间', ''].map((h, i) => (
                    <th key={i} className={`text-left text-xs font-mono font-bold tracking-widest uppercase px-4 py-3 ${i === 0 ? 'hidden sm:table-cell' : ''} ${i === 2 ? 'hidden sm:table-cell' : ''} ${i === 3 ? 'hidden md:table-cell' : ''} ${i === 5 ? 'hidden lg:table-cell' : ''}`}
                      style={{ color: ORANGE }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <AnimatePresence>
                  {filtered.map(item => (
                    <>
                      <motion.tr key={item.id} layout initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                        onClick={() => setExpandedId(expandedId === item.id ? null : item.id)}
                        className="border-b border-border cursor-pointer transition-colors"
                        style={{ background: expandedId === item.id ? ROW_HOVER : 'transparent' }}
                        onMouseEnter={e => { if (expandedId !== item.id) (e.currentTarget as HTMLElement).style.background = ROW_HOVER; }}
                        onMouseLeave={e => { if (expandedId !== item.id) (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
                      >
                        <td className="px-4 py-3 hidden sm:table-cell">
                          {((item as unknown as Record<string,unknown>).biz_no as string)
                            ? <span className="text-xs font-mono font-bold" style={{ color: ORANGE }}>{(item as unknown as Record<string,unknown>).biz_no as string}</span>
                            : <span className="text-xs font-mono" style={{ color: '#9CA3AF' }}>—</span>}
                        </td>
                        <td className="px-4 py-3">
                          <div className="font-medium" style={{ color: '#1A2337' }}>{item.name}</div>
                          {item.company && <div className="flex items-center gap-1 text-xs mt-0.5" style={{ color: '#5A6780' }}><Building2 className="w-3 h-3" />{item.company}</div>}
                        </td>
                        <td className="px-4 py-3 hidden sm:table-cell">
                          <div className="text-xs font-mono" style={{ color: '#5A6780' }}>{item.email}</div>
                          <div className="text-xs font-mono mt-0.5" style={{ color: '#1A2337' }}>{item.phone}</div>
                        </td>
                        <td className="px-4 py-3 hidden md:table-cell">
                          {item.material
                            ? <span className="text-xs font-mono px-1.5 py-0.5 rounded-sm border" style={{ background: `${ORANGE}18`, borderColor: `${ORANGE}44`, color: ORANGE }}>{item.material}</span>
                            : <span className="text-xs" style={{ color: '#5A6780' }}>—</span>}
                        </td>
                        <td className="px-4 py-3"><StatusBadge status={item.status} /></td>
                        <td className="px-4 py-3 hidden lg:table-cell">
                          <div className="flex items-center gap-1 text-xs" style={{ color: '#5A6780' }}>
                            <Clock className="w-3 h-3" />{timeAgo(item.created_at)}
                          </div>
                        </td>
                        <td className="px-4 py-3" style={{ color: '#5A6780' }}>
                          {expandedId === item.id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                        </td>
                      </motion.tr>

                      {expandedId === item.id && (
                        <motion.tr key={`${item.id}-detail`} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                          <td colSpan={7} className="px-6 py-5 border-b border-border" style={{ background: '#F7F8FA' }}>
                            <div className="grid md:grid-cols-2 gap-6">
                              <div className="space-y-2">
                                <p className="text-xs font-bold tracking-widest uppercase mb-3" style={{ color: '#5A6780' }}>联系信息</p>
                                <p className="text-sm font-medium" style={{ color: '#1A2337' }}>{item.name}{item.company && <span className="text-xs ml-2" style={{ color: '#5A6780' }}>· {item.company}</span>}</p>
                                <a href={`mailto:${item.email}`} className="block text-xs font-mono hover:underline" style={{ color: ORANGE }}>{item.email}</a>
                                <a href={`tel:${item.phone}`} className="block text-xs font-mono" style={{ color: '#1A2337' }}>{item.phone}</a>
                                {item.material && <span className="inline-block text-xs font-mono px-2 py-0.5 rounded-sm border" style={{ background: `${ORANGE}18`, borderColor: `${ORANGE}44`, color: ORANGE }}>{item.material}</span>}
                              </div>
                              <div className="space-y-4">
                                <p className="text-xs font-bold tracking-widest uppercase" style={{ color: '#5A6780' }}>咨询内容摘要</p>
                                <p className="text-sm leading-relaxed line-clamp-3" style={{ color: '#1A2337' }}>{item.message}</p>
                                <div className="flex flex-wrap items-center gap-3 pt-3 border-t border-border">
                                  <div className="flex items-center gap-2">
                                    <span className="text-xs" style={{ color: '#5A6780' }}>状态：</span>
                                    <StatusBadge status={item.status} />
                                  </div>
                                  <button onClick={() => setDrawerItem(item)}
                                    className="flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-sm transition-opacity hover:opacity-90"
                                    style={{ background: ORANGE, color: '#fff' }}
                                  ><FileText className="w-3.5 h-3.5" />详情 / 核价</button>
                                  {/* 转为订单按钮 — 一键直接转单，防重复，沿用biz_no */}
                                  {(() => {
                                    const isLost      = item.status === 'lost';
                                    const isConverted = !['new','quoted','lost'].includes(item.status);
                                    const hasPrice    = !!item.quoted_price;
                                    const canConvert  = !isLost && !isConverted && hasPrice;
                                    const tip = isLost ? '已流失线索不可转为订单'
                                      : isConverted ? '该线索已转为订单'
                                      : !hasPrice ? '请先完成核价再转为订单' : '';
                                    return (
                                      <button
                                        title={tip}
                                        disabled={!canConvert}
                                        onClick={async () => {
                                          if (!canConvert) return;
                                          const bizNo = (item as unknown as Record<string,unknown>).biz_no as string | null;
                                          // 防重：已有相同订单号则只更新状态，不重复创建
                                          if (bizNo) {
                                            const { count: existCount } = await supabase
                                              .from('orders').select('id', { count: 'exact', head: true })
                                              .eq('order_no', bizNo);
                                            if ((existCount ?? 0) > 0) {
                                              setInquiries(prev => prev.map(q =>
                                                q.id === item.id ? { ...q, status: 'pending' as InquiryStatus } : q
                                              ));
                                              return;
                                            }
                                          }
                                          // 从 quote_lines 构建明细行
                                          const rawLines = (item as unknown as Record<string,unknown>).quote_lines as Array<{material:string;spec:string;qty:string;unit:string}> | null;
                                          let itemRows: {material_code:string;specifications:string|null;quantity_kg:number|null;unit_price:number|null}[] = [];
                                          if (rawLines && rawLines.length > 0) {
                                            itemRows = rawLines.map(ql => ({
                                              material_code:  ql.material ?? '',
                                              specifications: ql.spec     || null,
                                              quantity_kg:    parseFloat(ql.qty)  || null,
                                              unit_price:     parseFloat(ql.unit) || null,
                                            }));
                                          } else {
                                            itemRows = [{
                                              material_code:  item.material ?? '',
                                              specifications: null,
                                              quantity_kg:    null,
                                              unit_price:     item.quoted_price ?? null,
                                            }];
                                          }
                                          const grandTotal = itemRows.reduce((s, r) =>
                                            s + ((r.quantity_kg ?? 0) * (r.unit_price ?? 0)), 0);
                                          const orderNo = bizNo ?? `BY-${new Date().toISOString().slice(0,10).replace(/-/g,'')}-000-0000`;
                                          const { data: orderData, error: orderErr } = await supabase
                                            .from('orders')
                                            .insert([{
                                              inquiry_id:   item.id,
                                              order_no:     orderNo,
                                              total_amount: grandTotal > 0 ? grandTotal : null,
                                            }])
                                            .select('id').single();
                                          if (orderErr || !orderData) return;
                                          await supabase.from('order_items').insert(
                                            itemRows.map(r => ({ ...r, order_id: (orderData as unknown as Record<string,unknown>).id as string }))
                                          );
                                          await supabase.from('inquiries').update({ status: 'pending' }).eq('id', item.id);
                                          setInquiries(prev => prev.map(q =>
                                            q.id === item.id ? { ...q, status: 'pending' as InquiryStatus } : q
                                          ));
                                        }}
                                        className="flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-sm transition-opacity"
                                        style={{
                                          background: isConverted ? '#D1FAE5' : canConvert ? '#16A34A' : '#F0F2F5',
                                          color: isConverted ? '#065F46' : canConvert ? '#fff' : '#9CA3AF',
                                          cursor: canConvert ? 'pointer' : 'not-allowed',
                                        }}
                                      >
                                        <ShoppingCart className="w-3.5 h-3.5" />
                                        {isConverted ? '已转单' : '一键转单'}
                                      </button>
                                    );
                                  })()}
                                  {/* 流失按钮 */}
                                  {item.status !== 'lost' && item.status !== 'completed' && (
                                    <button
                                      onClick={async () => {
                                        await supabase.from('inquiries').update({ status: 'lost' }).eq('id', item.id);
                                        setInquiries(prev => prev.map(q => q.id === item.id ? { ...q, status: 'lost' as InquiryStatus } : q));
                                      }}
                                      className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-sm border border-gray-300 text-gray-500 hover:bg-gray-100 transition-colors"
                                    ><XCircle className="w-3.5 h-3.5" />流失</button>
                                  )}
                                </div>
                              </div>
                            </div>
                          </td>
                        </motion.tr>
                      )}
                    </>
                  ))}
                </AnimatePresence>
              </tbody>
            </table>
          </div>
          <div className="px-4 py-3 border-t border-border flex items-center justify-between text-xs" style={{ background: HEADER_BG, color: '#5A6780' }}>
            <span>共 <span className="font-bold" style={{ color: '#1A2337' }}>{filtered.length}</span> 条线索</span>
            <span className="font-mono hidden sm:block">点击行展开 · 点击"详情/核价"生成报价单 PDF</span>
          </div>
        </div>
      )}

      <InquiryDrawer item={drawerItem} onClose={() => setDrawerItem(null)} onSaved={() => { fetchInquiries(true); setDrawerItem(null); }} />

      {showAddLead && (
        <AddLeadModal
          onClose={() => setShowAddLead(false)}
          onSaved={() => { setShowAddLead(false); fetchInquiries(true); }}
        />
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════
// 子视图：商务报价测算（委托给 AdminBOM 组件，永久稳定）
// ══════════════════════════════════════════════════
function BOMView() {
  return <BOMPageView />;
}


// ══════════════════════════════════════════════════
// 子视图：用户管理（仅 admin）
// ══════════════════════════════════════════════════
function UsersView() {
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editRole, setEditRole] = useState<UserRole>('sales');
  const [saving, setSaving] = useState(false);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase.from('user_profiles').select('*').order('created_at');
    if (data) setUsers(data as UserProfile[]);
    setLoading(false);
  }, []);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const saveRole = async (id: string) => {
    setSaving(true);
    await supabase.from('user_profiles').update({ role: editRole }).eq('id', id);
    setSaving(false); setEditingId(null); fetchUsers();
  };

  const ROLE_COLORS: Record<UserRole, string> = {
    admin: 'text-orange-400 bg-orange-400/10 border-orange-400/30',
    sales: 'text-blue-400 bg-blue-400/10 border-blue-400/30',
    factory: 'text-green-400 bg-green-400/10 border-green-400/30',
  };

  return (
    <div className="space-y-6">
      <div className="rounded-sm border border-border overflow-hidden" style={{ background: CARD_BG }}>
        {loading ? (
          <div className="p-16 flex justify-center"><RefreshCw className="w-8 h-8 animate-spin" style={{ color: ORANGE }} /></div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border" style={{ background: HEADER_BG }}>
                {['邮箱', '显示名称', '角色', '注册时间', '操作'].map(h => (
                  <th key={h} className="text-left text-xs font-mono font-bold tracking-widest uppercase px-4 py-3" style={{ color: ORANGE }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {users.map(u => (
                <tr key={u.id} className="border-b border-border transition-colors"
                  onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = ROW_HOVER; }}
                  onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
                >
                  <td className="px-4 py-3 text-xs font-mono" style={{ color: '#1A2337' }}>{u.email}</td>
                  <td className="px-4 py-3 text-xs" style={{ color: '#5A6780' }}>{u.display_name ?? '—'}</td>
                  <td className="px-4 py-3">
                    {editingId === u.id ? (
                      <select value={editRole} onChange={e => setEditRole(e.target.value as UserRole)}
                        className="text-xs rounded-sm px-2 py-1 outline-none"
                        style={{ background: '#FFFFFF', border: '1px solid #D0D5E2', color: '#1A2337' }}
                      >
                        <option value="sales">sales（销售）</option>
                        <option value="factory">factory（工厂）</option>
                        <option value="admin">admin（管理员）</option>
                      </select>
                    ) : (
                      <span className={`inline-block text-xs font-bold px-2 py-0.5 rounded-sm border ${ROLE_COLORS[u.role]}`}>{u.role}</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs" style={{ color: '#5A6780' }}>{new Date(u.created_at).toLocaleDateString('zh-CN')}</td>
                  <td className="px-4 py-3">
                    {editingId === u.id ? (
                      <div className="flex items-center gap-2">
                        <button onClick={() => saveRole(u.id)} disabled={saving}
                          className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-sm font-bold"
                          style={{ background: ORANGE, color: '#fff' }}
                        >{saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}保存</button>
                        <button onClick={() => setEditingId(null)} className="text-xs px-2 py-1.5 rounded-sm border border-border" style={{ color: '#5A6780' }}>取消</button>
                      </div>
                    ) : (
                      <button onClick={() => { setEditingId(u.id); setEditRole(u.role); }}
                        className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-sm border border-border hover:border-orange-400/30 transition-colors"
                        style={{ color: '#5A6780' }}
                      ><Pencil className="w-3 h-3" />修改角色</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      <div className="rounded-sm border border-blue-400/20 p-4 text-xs space-y-1" style={{ background: 'rgba(96,165,250,0.05)' }}>
        <p className="font-bold" style={{ color: '#60A5FA' }}>角色权限说明</p>
        <p style={{ color: '#5A6780' }}><span className="font-bold text-orange-400">admin</span> — 可访问全部模块（线索看板 + BOM维护 + 用户管理）</p>
        <p style={{ color: '#5A6780' }}><span className="font-bold text-blue-400">sales</span> — 仅可访问线索看板，查看客户线索并生成报价单</p>
        <p style={{ color: '#5A6780' }}><span className="font-bold text-green-400">factory</span> — 仅可访问BOM成本维护，管理底层物料成本数据</p>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════
// 主页面：带左侧导航的多模块后台
// ══════════════════════════════════════════════════
export default function AdminDashboard() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<ActiveTab>('crm');
  const [userEmail, setUserEmail] = useState('');
  const [userRole, setUserRole] = useState<UserRole>('sales');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [roleLoading, setRoleLoading] = useState(true);
  const [preselectedInquiryId, setPreselectedInquiryId] = useState<string>('');

  useEffect(() => {
    supabase.auth.getUser().then(async ({ data }) => {
      if (!data.user) { navigate('/admin/login', { replace: true }); return; }
      setUserEmail(data.user.email ?? '');
      const { data: profile } = await supabase.from('user_profiles').select('role').eq('id', data.user.id).single();
      const role = (profile?.role ?? 'sales') as UserRole;
      setUserRole(role);
      // 根据角色设置默认 tab
      if (role === 'factory') setActiveTab('bom');
      else if (role === 'admin') setActiveTab('overview');
      setRoleLoading(false);
    });
  }, [navigate]);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/admin/login', { replace: true });
  };

  // 根据角色定义可见菜单（含分组）
  type NavGroup = { group: string; items: { key: ActiveTab; label: string; sublabel: string; icon: React.ElementType; roles: UserRole[] }[] };
  const NAV_GROUPS: NavGroup[] = [
    {
      group: '销售与客户',
      items: [
        { key: 'overview',    label: '数据仪表盘',   sublabel: 'Overview',   icon: BarChart3,       roles: ['admin'] },
        { key: 'customers',  label: '客户管理',     sublabel: 'Customers',  icon: Users,           roles: ['sales', 'admin'] },
        { key: 'crm',        label: '线索看板',     sublabel: 'CRM',        icon: LayoutDashboard, roles: ['sales', 'admin'] },
        { key: 'orders',     label: '销售订单',     sublabel: 'Orders',     icon: ShoppingCart,    roles: ['sales', 'admin'] },
        { key: 'logistics',  label: '物流发货',     sublabel: 'Logistics',  icon: Truck,           roles: ['sales', 'admin'] },
      ]
    },
    {
      group: '工厂与生产',
      items: [
        { key: 'production', label: '生产看板',     sublabel: 'Production', icon: Wrench,          roles: ['factory', 'admin'] },
        { key: 'bom',        label: '商务报价测算',  sublabel: 'Quote Estimator', icon: Factory,    roles: ['factory', 'admin'] },
      ]
    },
    {
      group: '高级模块',
      items: [
        { key: 'tracker',    label: '流程追踪器',    sublabel: 'Tracker',    icon: GitBranch,       roles: ['sales', 'admin'] },
        { key: 'recipes',    label: 'BOM&动态成本核算', sublabel: 'BOM & Dynamic Cost', icon: FlaskConical, roles: ['factory', 'admin'] },
      ]
    },
    {
      group: '系统管理',
      items: [
        { key: 'users',      label: '用户管理',     sublabel: 'Users',      icon: ShieldCheck,     roles: ['admin'] },
      ]
    },
  ];

  const PAGE_TITLES: Record<ActiveTab, string> = {
    overview: '数据仪表盘',
    crm: '线索 CRM 看板',
    orders: '销售订单看板',
    logistics: '物流发货看板',
    production: '工厂生产看板',
    bom: '商务报价测算',
    users: '用户管理',
    tracker: '全链路流程追踪器',
    recipes: 'BOM & 动态成本核算',
    customers: '客户管理',
  };

  if (roleLoading) {
    return (
      <div className="admin-layout flex items-center justify-center">
        <RefreshCw className="w-8 h-8 animate-spin" style={{ color: ORANGE }} />
      </div>
    );
  }

  return (
    <div className="admin-layout flex">
      {/* ── 左侧导航 ── */}
      {/* 移动端遮罩 */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-20 bg-black/60 md:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      <aside className={`fixed md:sticky top-0 left-0 h-screen z-30 flex flex-col transition-transform duration-200
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
      `} style={{ width: 220, minWidth: 220, background: NAV_BG, borderRight: '1px solid #E5E7EB' }}>
        {/* Logo区 */}
        <div className="flex items-center justify-between px-5 h-14 border-b border-border">
          <div className="flex items-center gap-2.5">
            <div className="w-7 h-7 rounded-sm flex items-center justify-center flex-shrink-0" style={{ background: ORANGE }}>
              <span className="text-white font-black text-xs">P</span>
            </div>
            <div>
              <div className="font-black text-xs tracking-wider uppercase" style={{ color: '#1A2337' }}>PolyPrime</div>
              <div className="text-[10px] font-mono" style={{ color: '#5A6780' }}>管理后台</div>
            </div>
          </div>
          <button onClick={() => setSidebarOpen(false)} className="md:hidden" style={{ color: '#5A6780' }}><X className="w-4 h-4" /></button>
        </div>

        {/* 用户信息 */}
        <div className="px-5 py-4 border-b border-border">
          <div className="text-xs font-mono truncate" style={{ color: '#5A6780' }}>{userEmail}</div>
          <div className={`inline-block mt-1 text-[10px] font-bold px-2 py-0.5 rounded-sm border ${
            userRole === 'admin' ? 'text-orange-400 bg-orange-400/10 border-orange-400/30' :
            userRole === 'factory' ? 'text-green-400 bg-green-400/10 border-green-400/30' :
            'text-blue-400 bg-blue-400/10 border-blue-400/30'
          }`}>{userRole.toUpperCase()}</div>
        </div>

        {/* 导航菜单 */}
        <nav className="flex-1 px-2 py-3 overflow-y-auto">
          {NAV_GROUPS.map(({ group, items }) => {
            const visibleItems = items.filter(item => item.roles.includes(userRole));
            if (visibleItems.length === 0) return null;
            return (
              <div key={group} className="mb-4">
                <div className="px-3 mb-1">
                  <span className="text-[9px] font-mono font-bold tracking-[0.2em] uppercase" style={{ color: '#3A4A5C' }}>{group}</span>
                </div>
                <div className="space-y-0.5">
                  {visibleItems.map(({ key, label, sublabel, icon: Icon }) => {
                    const active = activeTab === key;
                    return (
                      <button key={key} onClick={() => { setActiveTab(key); setSidebarOpen(false); }}
                        className="w-full flex items-center gap-3 px-3 py-2 rounded-sm text-left transition-all"
                        style={{
                          background: active ? `${ORANGE}18` : 'transparent',
                          borderLeft: active ? `2px solid ${ORANGE}` : '2px solid transparent',
                          color: active ? ORANGE : '#8A9BB5',
                        }}
                        onMouseEnter={e => { if (!active) (e.currentTarget as HTMLElement).style.color = '#1A2337'; }}
                        onMouseLeave={e => { if (!active) (e.currentTarget as HTMLElement).style.color = '#8A9BB5'; }}
                      >
                        <Icon className="w-4 h-4 flex-shrink-0" />
                        <div>
                          <div className="text-xs font-semibold">{label}</div>
                          <div className="text-[10px] font-mono opacity-60">{sublabel}</div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </nav>

        {/* 底部退出 */}
        <div className="px-3 py-4 border-t border-border">
          <button onClick={handleLogout}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-sm text-left transition-colors"
            style={{ color: '#5A6780' }}
            onMouseEnter={e => { (e.currentTarget as HTMLElement).style.color = '#1A2337'; }}
            onMouseLeave={e => { (e.currentTarget as HTMLElement).style.color = '#8A9BB5'; }}
          >
            <LogOut className="w-4 h-4" />
            <span className="text-xs font-semibold">退出登录</span>
          </button>
        </div>
      </aside>

      {/* ── 右侧主内容区 ── */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* 顶栏 */}
        <header className="sticky top-0 z-20 flex items-center gap-4 px-6 h-14 border-b border-border" style={{ background: HEADER_BG }}>
          <button onClick={() => setSidebarOpen(true)} className="md:hidden" style={{ color: '#5A6780' }}>
            <Menu className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-sm font-bold" style={{ color: '#1A2337' }}>{PAGE_TITLES[activeTab]}</h1>
            <p className="text-[10px] font-mono" style={{ color: '#5A6780' }}>POLY-PRIME · Internal System</p>
          </div>
        </header>

        {/* 内容 */}
        <main className="flex-1 p-6">
          <AnimatePresence mode="wait">
            <motion.div key={activeTab} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.2 }}>
              {activeTab === 'overview'   && <OverviewView />}
              {activeTab === 'crm'        && <CRMView onConvertToOrder={(id) => { setPreselectedInquiryId(id); setActiveTab('orders'); }} />}
              {activeTab === 'orders'     && <OrdersView preselectedInquiryId={preselectedInquiryId} onPreselectedConsumed={() => setPreselectedInquiryId('')} />}
              {activeTab === 'logistics'  && <LogisticsView />}
              {activeTab === 'production' && <ProductionView />}
              {activeTab === 'bom'        && <BOMView />}
              {activeTab === 'users'      && <UsersView />}
              {activeTab === 'tracker'    && <TrackerView />}
              {activeTab === 'recipes'    && <RecipesView />}
              {activeTab === 'customers'  && <CustomersView />}
            </motion.div>
          </AnimatePresence>
        </main>
      </div>
    </div>
  );
}
